import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, Plus, Repeat, X } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import type { ReminderAnchor, ReminderDraft } from '@/src/renderer/lib/db/models'
import type { ReminderPermission } from '@/src/renderer/lib/platform'
import type { ReminderSilence } from '@/src/renderer/lib/reminders/schedule'
import type { ReminderRow } from '../../hooks/useTaskReminderRows'
import { ReminderDropdownContent, type ReminderDropdownTab } from '../ReminderDropdown'
import './ReminderList.scss'

export interface ReminderListContentProps {
  rows: readonly ReminderRow[]
  anchors: Record<ReminderAnchor, Date | null>
  anchorSilence: Record<ReminderAnchor, ReminderSilence | null>
  onAdd: (draft: ReminderDraft) => void
  onRemove: (reminderId: string) => void
  /**
   * Nur im Browser: ob er Mitteilungen zeigen darf. Ohne Erlaubnis bliebe jede Erinnerung stumm —
   * auch die Standard-Erinnerung, die ohne Klick entsteht. `undefined` am Desktop: kein Hinweis.
   */
  permission?: ReminderPermission
  /** Fragt den Browser — aus dem Klick heraus, sonst ignorieren ihn Safari und Firefox. */
  onRequestPermission?: () => void
  /** Den Bereich „+ Erinnerung“ gleich offen zeigen. */
  defaultAdding?: boolean
  /** Der Reiter, auf dem das Anlegen aufgeht. */
  defaultTab?: ReminderDropdownTab
  placement?: 'badge' | 'inline'
  /** Nur für Stories und Tests. */
  now?: Date
}

/**
 * **Die Erinnerungen einer Aufgabe** — der Inhalt des Chips „Erinnerung“ im Kopf der
 * Aufgaben-Details (Plan task-reminders, Entscheidung 12). Jede Zeile: was sie ist, wann sie als
 * Nächstes klingelt — oder gedämpft, warum sie still ist —, und × zum Entfernen. Darunter
 * „+ Erinnerung“ mit {@link ReminderDropdownContent}.
 *
 * @example
 * <ReminderListContent rows={rows.rows} anchors={rows.anchors} anchorSilence={rows.anchorSilence}
 *   onAdd={add} onRemove={remove} />
 */
export const ReminderListContent: React.FC<ReminderListContentProps> = ({
  rows, anchors, anchorSilence, onAdd, onRemove, permission, onRequestPermission,
  defaultAdding = false, defaultTab, placement = 'badge', now,
}) => {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(defaultAdding || rows.length === 0)
  const existing = rows
    .filter(row => row.reminder.kind === 'relative' && !row.standard)
    .map(row => ({ anchor: row.reminder.anchor ?? 'start', offsetMinutes: row.reminder.offsetMinutes ?? 0 }))

  return (
    <div className="reminder-list" data-placement={placement}>
      <div className="reminder-list__title">{t('reminders.list.title')}</div>

      {permission && permission !== 'granted' && (
        <div className="reminder-list__permission" data-state={permission}>
          <span>{t(`reminders.permission.${permission}`)}</span>
          {permission === 'default' && onRequestPermission && (
            <Button variant="outline" size="sm" onClick={onRequestPermission}>{t('reminders.permission.allow')}</Button>
          )}
        </div>
      )}

      {rows.length > 0 ? (
        <ul className="reminder-list__rows">
          {rows.map(row => (
            <li
              key={row.reminder.id}
              className={`reminder-list__row${row.status.silent ? ' reminder-list__row--silent' : ''}`}
            >
              {row.reminder.recurrence
                ? <Repeat size={14} className="reminder-list__icon" aria-hidden="true" />
                : <Bell size={14} className="reminder-list__icon" aria-hidden="true" />}
              <span className="reminder-list__label">{row.label}</span>
              <span className="reminder-list__status">{row.status.text}</span>
              <button
                type="button"
                className="reminder-list__remove"
                aria-label={t('reminders.list.remove', { label: row.label })}
                onClick={() => onRemove(row.reminder.id)}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="reminder-list__empty">{t('reminders.list.empty')}</p>
      )}

      {adding ? (
        <>
          <div className="context-menu-content__separator" />
          <ReminderDropdownContent
            anchors={anchors}
            anchorSilence={anchorSilence}
            existing={existing}
            onAdd={onAdd}
            placement={placement}
            defaultTab={defaultTab}
            now={now}
          />
        </>
      ) : (
        <button type="button" className="reminder-list__add" onClick={() => setAdding(true)}>
          <Plus size={14} aria-hidden="true" />
          {t('reminders.list.add')}
        </button>
      )}
    </div>
  )
}
